import { useTranslation } from 'react-i18next';
import { Button, StatusBadge } from '@xxtract/ui';
import { formatLeadTime, formatOrderDate } from '../format';
import { deriveOrderProgressBadge, deriveServiceCodes } from '../order-summary';
import type { OrderTree } from '../orders.api';
import { OrderStatusBadge } from './OrderStatusBadge';

interface OrderDetailHeaderProps {
  orderNumber: string;
  tree: OrderTree;
  locale: string;
  submittedAt: string | null;
  leadTimeMs: number | null;
  onBack: () => void;
}

/** fu-7: rich order-detail header (mockup order-detail.html) — back nav, dienst-chips, a
 * voortgang/kwaliteitsbadge, klantnaam (via glnProvider, GLN as subtext/fallback), ingediend-
 * datum and doorlooptijd. Every field degrades gracefully — a missing name/date/duration never
 * leaves the header empty or crashes it (AC1). */
export function OrderDetailHeader({ leadTimeMs, locale, onBack, orderNumber, submittedAt, tree }: OrderDetailHeaderProps) {
  const { t } = useTranslation();
  const serviceCodes = deriveServiceCodes(tree);
  const progressBadge = deriveOrderProgressBadge(tree);
  const customerName = tree.companyName ?? tree.gln ?? t('common.notAvailable');
  const showGlnSubtext = tree.companyName != null && tree.gln != null;

  return (
    <header className="flex items-start gap-3" data-testid="order-detail-header">
      <Button aria-label={t('orders.detail.back')} onClick={onBack} size="icon" variant="outline">
        <span aria-hidden="true" className="material-symbols-outlined text-[20px]">arrow_back</span>
      </Button>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('orders.detail.title', { orderNumber })}</h1>
          {serviceCodes.map((serviceCode) => (
            <span className="inline-flex items-center rounded-md bg-[var(--primary-light)] px-2 py-0.5 text-[11px] font-semibold text-[var(--primary)]" key={serviceCode}>{serviceCode}</span>
          ))}
          {progressBadge != null
            ? <StatusBadge label={t(progressBadge.labelKey, progressBadge.labelParams)} status={progressBadge.status} />
            : <OrderStatusBadge state={tree.status} />}
        </div>
        <p className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-[var(--foreground-muted)]">
          <span>{customerName}</span>
          {showGlnSubtext ? <span>{t('orders.detail.glnLabel', { gln: tree.gln })}</span> : null}
          {submittedAt != null ? <span>{t('orders.detail.submittedAt', { date: formatOrderDate(submittedAt, locale) })}</span> : null}
          {leadTimeMs != null ? <span>{t('orders.detail.leadTime', { duration: formatLeadTime(leadTimeMs, t) })}</span> : null}
        </p>
      </div>
    </header>
  );
}
